import {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";

/**
 * Trace ID — cross-stack request correlation.
 *
 * The backend resolves a trace id per request in this priority order
 * (`dockerized-dispatcher/hg/core/Http/TraceId.php`):
 *
 *   1. `trace-id` request header        (if a valid 32-char lowercase hex string)
 *   2. `X-AppGw-Trace-Id` request header (same validity rule)
 *   3. A freshly generated UUID v4 with hyphens stripped
 *
 * By sending `trace-id` ourselves we win at step 1, so a browser action and every
 * log line it produces downstream (dispatcher → router → websocket server) share
 * one id. An absent or malformed header is never an error — the backend silently
 * falls through and generates its own, we just lose the correlation.
 *
 * The id is **per request**, not per session, matching the backend's request-scoped
 * model. Use the existing `sessionId` in localStorage to group a tab's activity.
 */

export const TRACE_ID_HEADER = "trace-id";

/**
 * Generates a UUID v4 with hyphens stripped — a 32-char lowercase hex string,
 * the only shape the backend accepts.
 *
 * Uses `crypto.getRandomValues` rather than `crypto.randomUUID` because the latter
 * is unavailable in non-secure contexts, and a missing trace id silently breaks
 * correlation rather than failing loudly.
 */
export function newTraceId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  // Apply UUID v4 version and variant bits, mirroring the backend generator.
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant bits

  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Recovers the trace id that was sent on a request, for use when reporting its
 * failure. Reads it back off the request config rather than tracking it separately,
 * so the logged id is provably the one that went over the wire.
 */
function readTraceId(config?: InternalAxiosRequestConfig): string {
  const headers = config?.headers;
  if (!headers) return "";

  // AxiosHeaders on the normal path; tolerate a plain object defensively, since a
  // failure report is the worst place to throw a second error.
  const value =
    typeof headers.get === "function"
      ? headers.get(TRACE_ID_HEADER)
      : (headers as Record<string, unknown>)[TRACE_ID_HEADER];

  return typeof value === "string" ? value : "";
}

/**
 * Best-effort extraction of the API command from a failed request.
 *
 * Every webmsg/webadmin call POSTs to the same URL and identifies the operation with
 * a `cmd` field, so the URL alone cannot tell you what failed. By the time a response
 * interceptor runs, axios has already serialized the body, hence the string parse.
 * Returns undefined for bodies with no `cmd` (e.g. the JSON /emit-message payload).
 */
function readCommand(config?: InternalAxiosRequestConfig): string | undefined {
  const data = config?.data;

  if (typeof data === "string") {
    return new URLSearchParams(data).get("cmd") ?? undefined;
  }
  if (data && typeof data === "object") {
    return (data as { cmd?: string }).cmd;
  }
  return undefined;
}

/**
 * Wires an axios instance into the trace: stamps a fresh id on every outgoing
 * request, and reports that id whenever a request fails.
 *
 * Both halves live here because they are only useful together — generating an id
 * nothing records, or reporting an id that was never sent, is worse than neither.
 *
 * Called at instance creation so no import-order dependency exists — an instance
 * can never send an untraced request while waiting for setup to run.
 */
export function attachTraceId(instance: AxiosInstance): void {
  instance.interceptors.request.use((config) => {
    config.headers.set(TRACE_ID_HEADER, newTraceId());
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      // The router signals most application-level failures as HTTP 200 with an
      // `error` in the body (see the `response.error` throws in chatSlice), so a
      // rejection-only hook would miss the failures that happen most often.
      if (response.data?.error) {
        console.error("[http] Request returned an error", {
          traceId: readTraceId(response.config),
          cmd: readCommand(response.config),
          url: response.config?.url,
          error: response.data.error,
        });
      }

      return response;
    },
    (error: AxiosError) => {
      // Cancellations are deliberate, not failures worth a trace lookup.
      //
      // Checked by code rather than axios.isCancel: that guard is typed
      // `value is Cancel`, and AxiosError structurally satisfies Cancel (both carry
      // a `message`), so TypeScript narrows the negative branch to `never` and every
      // field access below fails to compile.
      if (error.code !== AxiosError.ERR_CANCELED) {
        console.error("[http] Request failed", {
          traceId: readTraceId(error.config),
          cmd: readCommand(error.config),
          url: error.config?.url,
          status: error.response?.status,
          message: error.message,
        });
      }

      // Observe only. The rejection must propagate unchanged, or every existing
      // catch in the app silently stops seeing its errors.
      return Promise.reject(error);
    }
  );
}
